import React from "react";
import { Box, Container, Text, SimpleGrid, Flex, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const MotionBox = motion(Box);

const mcqTraits = ["Concept-driven", "Exam-oriented", "Built for real understanding"];
const benefits = [
  "Recognise patterns",
  "Avoid common mistakes",
  "Understand why one answer works — and others don't",
];

const sampleQuestion = {
  q: "A nurse is assessing a patient with suspected deep vein thrombosis. Which finding is MOST significant?",
  options: [
    { label: "A", text: "Pallor of the affected limb", correct: false },
    { label: "B", text: "Unilateral leg swelling with warmth and tenderness", correct: true },
    { label: "C", text: "Bilateral ankle oedema", correct: false },
    { label: "D", text: "Decreased peripheral pulses", correct: false },
  ],
  explanation: "Unilateral swelling, warmth, and tenderness are classic DVT signs — reflecting localised venous obstruction and inflammation.",
};

export const MCQ = () => {
  return (
    <Box id="mcqs" bg="#FFFFFF" py={{ base: "64px", md: "96px" }}>
      <Container maxW="1200px" px={{ base: "5", md: "8" }}>

        <Flex direction="column" align="center" textAlign="center" mb={{ base: 12, md: 16 }}>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="0.8125rem"
            fontWeight="600"
            letterSpacing="0.1em"
            textTransform="uppercase"
            color="#0095C8"
            mb={3}
          >
            MCQ Practice
          </Text>
          <Text
            as="h2"
            fontFamily="'Public Sans', sans-serif"
            fontWeight="600"
            fontSize={{ base: "1.75rem", md: "2.375rem" }}
            color="#293452"
            lineHeight="1.25"
            maxW="700px"
          >
            Practice the way you'll be tested —{" "}
            <Box as="span" color="#0095C8">and the way you'll think on duty</Box>
          </Text>
        </Flex>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 10, lg: 16 }} alignItems="start">

          {/* Left — feature list */}
          <VStack align="stretch" spacing={8}>
            <Box>
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize="0.875rem"
                color="rgba(41,52,82,0.55)"
                fontWeight="500"
                mb={4}
              >
                Our MCQs are:
              </Text>
              <VStack align="stretch" spacing={3}>
                {mcqTraits.map((trait, i) => (
                  <MotionBox
                    key={trait}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <Flex
                      align="center"
                      gap={4}
                      p={4}
                      bg="#f0f9ff"
                      borderRadius="10px"
                      border="1px solid rgba(0,149,200,0.12)"
                      borderLeft="3px solid #0095C8"
                    >
                      <Box color="#0095C8" flexShrink={0}>
                        <CheckCircle2 size={18} strokeWidth={2} />
                      </Box>
                      <Text
                        fontFamily="'Inter', sans-serif"
                        fontSize="0.9375rem"
                        color="#293452"
                        fontWeight="500"
                      >
                        {trait}
                      </Text>
                    </Flex>
                  </MotionBox>
                ))}
              </VStack>
            </Box>

            <Box>
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize="0.875rem"
                color="rgba(41,52,82,0.55)"
                fontWeight="500"
                mb={4}
              >
                Every question helps you:
              </Text>
              <VStack align="stretch" spacing={3}>
                {benefits.map((benefit, i) => (
                  <MotionBox
                    key={benefit}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <Flex
                      align="center"
                      gap={4}
                      p={4}
                      bg="#f5f3ff"
                      borderRadius="10px"
                      border="1px solid rgba(147,124,230,0.15)"
                      borderLeft="3px solid #937CE6"
                    >
                      <Box color="#937CE6" flexShrink={0}>
                        <CheckCircle2 size={18} strokeWidth={2} />
                      </Box>
                      <Text
                        fontFamily="'Inter', sans-serif"
                        fontSize="0.9375rem"
                        color="#293452"
                      >
                        {benefit}
                      </Text>
                    </Flex>
                  </MotionBox>
                ))}
              </VStack>
            </Box>

            <MotionBox
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Text
                fontFamily="'Public Sans', sans-serif"
                fontWeight="600"
                fontSize={{ base: "1rem", md: "1.125rem" }}
                bgGradient="linear(135deg, #937CE6, #F24C96)"
                bgClip="text"
              >
                Because in exams — and in patient care — guessing isn't enough.
              </Text>
            </MotionBox>
          </VStack>

          {/* Right — MCQ mockup card */}
          <MotionBox
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Box
              bg="#2F3859"
              borderRadius="16px"
              p={{ base: 5, md: 7 }}
              border="1px solid rgba(128,156,255,0.15)"
              boxShadow="0 16px 48px rgba(41,52,82,0.18)"
              position="relative"
              overflow="hidden"
            >
              {/* Top accent */}
              <Box
                position="absolute"
                top="0" left="0" right="0"
                h="3px"
                bgGradient="linear(to-r, #0095C8, #809CFF)"
              />

              {/* Header */}
              <Flex align="center" justify="space-between" mb={5}>
                <Box
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="rgba(0,149,200,0.15)"
                  border="1px solid rgba(0,149,200,0.3)"
                >
                  <Text
                    fontFamily="'Inter', sans-serif"
                    fontSize="0.75rem"
                    fontWeight="600"
                    color="#0095C8"
                  >
                    Medical-Surgical Nursing
                  </Text>
                </Box>
                <Text
                  fontFamily="'Inter', sans-serif"
                  fontSize="0.75rem"
                  color="rgba(255,255,255,0.35)"
                >
                  Q 12 of 40
                </Text>
              </Flex>

              {/* Question */}
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize={{ base: "0.875rem", md: "0.9375rem" }}
                color="rgba(255,255,255,0.88)"
                lineHeight="1.7"
                mb={5}
                fontWeight="500"
              >
                {sampleQuestion.q}
              </Text>

              {/* Options */}
              <VStack align="stretch" spacing={2} mb={5}>
                {sampleQuestion.options.map((opt) => (
                  <Flex
                    key={opt.label}
                    align="center"
                    gap={3}
                    p={3}
                    borderRadius="8px"
                    bg={opt.correct ? "rgba(0,149,200,0.15)" : "rgba(255,255,255,0.04)"}
                    border={opt.correct ? "1px solid rgba(0,149,200,0.4)" : "1px solid rgba(255,255,255,0.06)"}
                    cursor="default"
                  >
                    <Box
                      w="28px"
                      h="28px"
                      borderRadius="50%"
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg={opt.correct ? "rgba(0,149,200,0.25)" : "rgba(255,255,255,0.06)"}
                      border={opt.correct ? "1.5px solid #0095C8" : "1.5px solid rgba(255,255,255,0.12)"}
                    >
                      {opt.correct ? (
                        <CheckCircle2 size={14} color="#0095C8" strokeWidth={2.5} />
                      ) : (
                        <Text
                          fontFamily="'Inter', sans-serif"
                          fontSize="0.7rem"
                          fontWeight="600"
                          color="rgba(255,255,255,0.4)"
                        >
                          {opt.label}
                        </Text>
                      )}
                    </Box>
                    <Text
                      fontFamily="'Inter', sans-serif"
                      fontSize="0.875rem"
                      color={opt.correct ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.55)"}
                      fontWeight={opt.correct ? "500" : "400"}
                    >
                      {opt.text}
                    </Text>
                  </Flex>
                ))}
              </VStack>

              {/* Explanation */}
              <Box
                p={4}
                bg="rgba(0,149,200,0.08)"
                borderRadius="8px"
                borderLeft="3px solid #0095C8"
              >
                <Text
                  fontFamily="'Inter', sans-serif"
                  fontSize="0.8rem"
                  fontWeight="600"
                  color="#0095C8"
                  mb={1}
                >
                  Why B is correct:
                </Text>
                <Text
                  fontFamily="'Inter', sans-serif"
                  fontSize="0.8rem"
                  color="rgba(255,255,255,0.65)"
                  lineHeight="1.6"
                >
                  {sampleQuestion.explanation}
                </Text>
              </Box>
            </Box>
          </MotionBox>

        </SimpleGrid>
      </Container>
    </Box>
  );
};
